import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Cloud, Server, Link2, Unlink, RefreshCw, CheckCircle2, XCircle, 
  Clock, AlertTriangle, Settings, FolderKanban, History, BarChart3,
  ChevronRight, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JiraSettings = () => {
  const [status, setStatus] = useState(null);
  const [projects, setProjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [scopeOpsProjects, setScopeOpsProjects] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [telemetry, setTelemetry] = useState(null);
  const [schedulerState, setSchedulerState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dcForm, setDcForm] = useState({ base_url: '', pat: '', display_name: '' });
  const [mappingForm, setMappingForm] = useState({
    jira_project_id: '',
    jira_project_key: '',
    jira_project_name: '',
    scopeops_project_id: '',
    connection_type: '',
    cloud_id: '',
    base_url: '',
    default_issue_type: 'Epic'  // E2E mapping field
  });
  const [showAdvancedMapping, setShowAdvancedMapping] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  // Fetch connection status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  }, [getAuthHeaders]);

  // Fetch Jira projects
  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/projects`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  }, [getAuthHeaders]);

  // Fetch mappings
  const fetchMappings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/mappings`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMappings(data.mappings || []);
      }
    } catch (err) {
      console.error('Failed to fetch mappings:', err);
    }
  }, [getAuthHeaders]);

  // Fetch ScopeOps projects for mapping
  const fetchScopeOpsProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/projects`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setScopeOpsProjects(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch ScopeOps projects:', err);
    }
  }, [getAuthHeaders]);

  // Fetch sync history
  const fetchSyncHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/sync/history?limit=10`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSyncHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch sync history:', err);
    }
  }, [getAuthHeaders]);

  // Fetch telemetry
  const fetchTelemetry = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/telemetry`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTelemetry(data);
      }
    } catch (err) {
      console.error('Failed to fetch telemetry:', err);
    }
  }, [getAuthHeaders]);

  // Fetch scheduler state
  const fetchSchedulerState = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/sync/scheduler`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSchedulerState(data);
      }
    } catch (err) {
      console.error('Failed to fetch scheduler state:', err);
    }
  }, [getAuthHeaders]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStatus(),
        fetchMappings(),
        fetchScopeOpsProjects(),
        fetchSyncHistory(),
        fetchTelemetry(),
        fetchSchedulerState()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchMappings, fetchScopeOpsProjects, fetchSyncHistory, fetchTelemetry, fetchSchedulerState]);

  // Fetch projects when status changes
  useEffect(() => {
    if (status?.any_connected) {
      fetchProjects();
    }
  }, [status, fetchProjects]);

  // Connect Jira Cloud
  const connectCloud = async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/cloud/connect`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        setError('Failed to initiate Jira Cloud connection');
      }
    } catch (err) {
      setError('Connection error: ' + err.message);
    }
  };

  // Connect Jira Data Center
  const connectDC = async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/dc/connect`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(dcForm)
      });
      if (response.ok) {
        await fetchStatus();
        setDcForm({ base_url: '', pat: '', display_name: '' });
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to connect');
      }
    } catch (err) {
      setError('Connection error: ' + err.message);
    }
  };

  // Disconnect Cloud
  const disconnectCloud = async () => {
    try {
      await fetch(`${API_URL}/api/integrations/jira/cloud/disconnect`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      await fetchStatus();
    } catch (err) {
      setError('Disconnect error: ' + err.message);
    }
  };

  // Disconnect DC
  const disconnectDC = async (baseUrl) => {
    try {
      await fetch(`${API_URL}/api/integrations/jira/dc/disconnect`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base_url: baseUrl })
      });
      await fetchStatus();
    } catch (err) {
      setError('Disconnect error: ' + err.message);
    }
  };

  // Create mapping
  const createMapping = async () => {
    try {
      const response = await fetch(`${API_URL}/api/integrations/jira/map`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(mappingForm)
      });
      if (response.ok) {
        await fetchMappings();
        setMappingForm({
          jira_project_id: '',
          jira_project_key: '',
          jira_project_name: '',
          scopeops_project_id: '',
          connection_type: '',
          cloud_id: '',
          base_url: '',
          default_issue_type: 'Epic'
        });
        setShowAdvancedMapping(false);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create mapping');
      }
    } catch (err) {
      setError('Mapping error: ' + err.message);
    }
  };

  // Delete mapping
  const deleteMapping = async (mappingId) => {
    try {
      await fetch(`${API_URL}/api/integrations/jira/mappings/${mappingId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await fetchMappings();
    } catch (err) {
      setError('Delete error: ' + err.message);
    }
  };

  // Trigger sync
  const triggerSync = async (mappingId = null) => {
    try {
      const url = mappingId 
        ? `${API_URL}/api/integrations/jira/sync/${mappingId}`
        : `${API_URL}/api/integrations/jira/sync/trigger_all`;
      
      await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      setTimeout(() => {
        fetchMappings();
        fetchSyncHistory();
        fetchTelemetry();
      }, 2000);
    } catch (err) {
      setError('Sync error: ' + err.message);
    }
  };

  // Handle project selection for mapping
  const handleProjectSelect = (project) => {
    setMappingForm({
      ...mappingForm,
      jira_project_id: project.id,
      jira_project_key: project.key,
      jira_project_name: project.name,
      connection_type: project.type,
      cloud_id: project.cloud_id || '',
      base_url: project.base_url || ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="jira-settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Jira Integration</h1>
          <p className="text-gray-500">Connect and sync with Jira Cloud or Data Center</p>
        </div>
        <Badge variant={status?.any_connected ? "default" : "secondary"}>
          {status?.any_connected ? "Connected" : "Not Connected"}
        </Badge>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}

      <Tabs defaultValue="connection" className="space-y-4">
        <TabsList>
          <TabsTrigger value="connection" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Connection
          </TabsTrigger>
          <TabsTrigger value="mapping" className="flex items-center gap-2">
            <FolderKanban className="h-4 w-4" />
            Project Mapping
          </TabsTrigger>
          <TabsTrigger value="sync" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Sync History
          </TabsTrigger>
          <TabsTrigger value="telemetry" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Telemetry
          </TabsTrigger>
        </TabsList>

        {/* Connection Tab */}
        <TabsContent value="connection" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Jira Cloud Card */}
            <Card data-testid="jira-cloud-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="h-5 w-5" />
                  Jira Cloud
                </CardTitle>
                <CardDescription>Connect via OAuth 2.0</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {status?.cloud?.connected ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Connected
                    </div>
                    {status.cloud.sites?.map((site, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded">
                        <p className="font-medium">{site.name}</p>
                        <p className="text-sm text-gray-500">{site.url}</p>
                      </div>
                    ))}
                    <Button variant="destructive" onClick={disconnectCloud} data-testid="disconnect-cloud-btn">
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button onClick={connectCloud} data-testid="connect-cloud-btn">
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect Jira Cloud
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Jira Data Center Card */}
            <Card data-testid="jira-dc-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Jira Data Center
                </CardTitle>
                <CardDescription>Connect via Personal Access Token (PAT)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {status?.datacenter?.instances?.map((instance, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded flex justify-between items-center">
                    <div>
                      <p className="font-medium">{instance.name}</p>
                      <p className="text-sm text-gray-500">{instance.base_url}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => disconnectDC(instance.base_url)}
                      data-testid={`disconnect-dc-${idx}`}
                    >
                      <Unlink className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                
                <div className="space-y-3 pt-4 border-t">
                  <Input
                    placeholder="https://jira.company.com"
                    value={dcForm.base_url}
                    onChange={(e) => setDcForm({ ...dcForm, base_url: e.target.value })}
                    data-testid="dc-base-url-input"
                  />
                  <Input
                    placeholder="Personal Access Token"
                    type="password"
                    value={dcForm.pat}
                    onChange={(e) => setDcForm({ ...dcForm, pat: e.target.value })}
                    data-testid="dc-pat-input"
                  />
                  <Input
                    placeholder="Display Name (optional)"
                    value={dcForm.display_name}
                    onChange={(e) => setDcForm({ ...dcForm, display_name: e.target.value })}
                    data-testid="dc-display-name-input"
                  />
                  <Button 
                    onClick={connectDC} 
                    disabled={!dcForm.base_url || !dcForm.pat}
                    data-testid="connect-dc-btn"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Project Mapping Tab */}
        <TabsContent value="mapping" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Project Mappings</CardTitle>
              <CardDescription>Map Jira projects to ScopeOps projects</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Mappings */}
              <div className="space-y-2">
                {mappings.length === 0 ? (
                  <p className="text-gray-500 text-sm">No mappings configured yet.</p>
                ) : (
                  mappings.map((mapping) => (
                    <div 
                      key={mapping.mapping_id} 
                      className="bg-gray-50 p-4 rounded flex justify-between items-center"
                      data-testid={`mapping-${mapping.mapping_id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{mapping.jira_project_name}</span>
                          <Badge variant="outline">{mapping.jira_project_key}</Badge>
                          <span className="text-gray-400">→</span>
                          <span className="text-gray-600">{mapping.scopeops_project_id}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            {mapping.connection_type === 'cloud' ? <Cloud className="h-3 w-3" /> : <Server className="h-3 w-3" />}
                            {mapping.connection_type}
                          </span>
                          {mapping.default_issue_type && (
                            <span className="px-2 py-0.5 bg-teal-50 text-teal-600 rounded text-xs">
                              Type: {mapping.default_issue_type}
                            </span>
                          )}
                          {mapping.last_sync_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Last sync: {new Date(mapping.last_sync_at).toLocaleString()}
                            </span>
                          )}
                          {mapping.last_sync_status && (
                            <Badge variant={mapping.last_sync_status === 'success' ? 'default' : 'destructive'}>
                              {mapping.last_sync_status}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => triggerSync(mapping.mapping_id)}
                          data-testid={`sync-mapping-${mapping.mapping_id}`}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteMapping(mapping.mapping_id)}
                          data-testid={`delete-mapping-${mapping.mapping_id}`}
                        >
                          <XCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* New Mapping Form */}
              {status?.any_connected && (
                <div className="border-t pt-4 mt-4 space-y-4">
                  <h4 className="font-medium">Create New Mapping</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Jira Project</label>
                      <select 
                        className="w-full mt-1 p-2 border rounded"
                        value={mappingForm.jira_project_key}
                        onChange={(e) => {
                          const project = projects.find(p => p.key === e.target.value);
                          if (project) handleProjectSelect(project);
                        }}
                        data-testid="jira-project-select"
                      >
                        <option value="">Select Jira Project</option>
                        {projects.map((p) => (
                          <option key={p.key} value={p.key}>
                            {p.name} ({p.key}) - {p.type}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">ScopeOps Project</label>
                      <select 
                        className="w-full mt-1 p-2 border rounded"
                        value={mappingForm.scopeops_project_id}
                        onChange={(e) => setMappingForm({ ...mappingForm, scopeops_project_id: e.target.value })}
                        data-testid="scopeops-project-select"
                      >
                        <option value="">Select ScopeOps Project</option>
                        {scopeOpsProjects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.attributes?.name || p.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Advanced E2E Mapping Options */}
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setShowAdvancedMapping(!showAdvancedMapping)}
                      className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {showAdvancedMapping ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      Advanced Options
                    </button>
                    
                    {showAdvancedMapping && (
                      <div className="mt-3 p-4 bg-gray-50 rounded-lg border-dashed border">
                        <div>
                          <label className="text-sm font-medium">Default Issue Type for Demand Conversion</label>
                          <select 
                            className="w-full mt-1 p-2 border rounded max-w-xs"
                            value={mappingForm.default_issue_type}
                            onChange={(e) => setMappingForm({ ...mappingForm, default_issue_type: e.target.value })}
                            data-testid="default-issue-type-select"
                          >
                            <option value="Epic">Epic</option>
                            <option value="Story">Story</option>
                            <option value="Task">Task</option>
                            <option value="Bug">Bug</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            When converting a demand to Jira, this issue type will be used.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={createMapping}
                    disabled={!mappingForm.jira_project_key || !mappingForm.scopeops_project_id}
                    data-testid="create-mapping-btn"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Create Mapping
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Manual Sync All */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Manual Sync
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button onClick={() => triggerSync()} disabled={mappings.length === 0} data-testid="sync-all-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync All Mappings
              </Button>
              <p className="text-sm text-gray-500 mt-2">
                Automatic sync runs every 5 minutes when scheduler is enabled.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync History Tab */}
        <TabsContent value="sync" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sync Run History</CardTitle>
              <CardDescription>Recent synchronization runs</CardDescription>
            </CardHeader>
            <CardContent>
              {syncHistory.length === 0 ? (
                <p className="text-gray-500">No sync runs yet.</p>
              ) : (
                <div className="space-y-2">
                  {syncHistory.map((run) => (
                    <div key={run.run_id} className="bg-gray-50 p-3 rounded">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium">{run.jira_project_key}</span>
                          <Badge variant="outline" className="ml-2">{run.sync_type}</Badge>
                        </div>
                        <Badge variant={run.status === 'completed' ? 'default' : 'destructive'}>
                          {run.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex gap-4">
                        <span>Started: {new Date(run.started_at).toLocaleString()}</span>
                        {run.duration_ms && <span>Duration: {run.duration_ms}ms</span>}
                        {run.results && (
                          <span>
                            New: {run.results.new || 0}, Updated: {run.results.updated || 0}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scheduler State */}
          <Card>
            <CardHeader>
              <CardTitle>Scheduler Status</CardTitle>
            </CardHeader>
            <CardContent>
              {schedulerState ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={schedulerState.scheduler_enabled ? 'default' : 'secondary'}>
                      {schedulerState.scheduler_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    {schedulerState.is_running && (
                      <Badge variant="outline">Running</Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>Interval: {schedulerState.interval_minutes} minutes</p>
                    {schedulerState.last_run && (
                      <p>Last run: {new Date(schedulerState.last_run).toLocaleString()}</p>
                    )}
                    {schedulerState.last_record_count > 0 && (
                      <p>Last sync: {schedulerState.last_record_count} records</p>
                    )}
                    {schedulerState.consecutive_failures > 0 && (
                      <p className="text-red-500">Consecutive failures: {schedulerState.consecutive_failures}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Scheduler state unavailable.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Telemetry Tab */}
        <TabsContent value="telemetry" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Integration Telemetry</CardTitle>
              <CardDescription>Real-time metrics and counters</CardDescription>
            </CardHeader>
            <CardContent>
              {telemetry ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-2xl font-bold text-green-600">
                      {telemetry.sync?.['jira.pull.new'] || 0}
                    </p>
                    <p className="text-sm text-gray-500">New Items Synced</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-2xl font-bold text-blue-600">
                      {telemetry.sync?.['jira.pull.updated'] || 0}
                    </p>
                    <p className="text-sm text-gray-500">Items Updated</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-2xl font-bold text-red-600">
                      {telemetry.sync?.['jira.pull.errors'] || 0}
                    </p>
                    <p className="text-sm text-gray-500">Errors</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-2xl font-bold">
                      {telemetry.items?.linked || 0}
                    </p>
                    <p className="text-sm text-gray-500">Linked Items</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-2xl font-bold">
                      {telemetry.mappings?.active || 0}
                    </p>
                    <p className="text-sm text-gray-500">Active Mappings</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-2xl font-bold">
                      {telemetry.sync?.['jira.pull.total_runs'] || 0}
                    </p>
                    <p className="text-sm text-gray-500">Total Sync Runs</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Telemetry data unavailable.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default JiraSettings;
